import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { deleteUser, getAllUsers, updateUser } from '@/api/api'
import { UserListItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

import { AccessSettingsModal } from './components/access-settings-modal'
import { InviteUserDialog } from './components/invite-user-dialog'

export const AdminUsers = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserListItem[]>([])
  const [availableDomains, setAvailableDomains] = useState<string[]>([])
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([])
  const [saving, setSaving] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [accessModalUser, setAccessModalUser] = useState<UserListItem | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const loading = useAppStore((state) => state.loading)
  const startLoading = useAppStore((state) => state.startLoading)
  const stopLoading = useAppStore((state) => state.stopLoading)
  const currentUserId = useAppStore((state) => state.currentUserId)
  const refreshUserData = useAppStore((state) => state.refreshUserData)

  useEffect(() => {
    const fetchUsers = async () => {
      startLoading()
      try {
        const response = await getAllUsers()
        setUsers(response.users)
        setAvailableDomains(response.available_domains)
        setAvailableFeatures(response.available_features)
      } catch (error) {
        console.error('Failed to fetch users:', error)
      } finally {
        stopLoading()
      }
    }
    fetchUsers()
  }, [startLoading, stopLoading])

  const handleUpdateUser = useCallback(
    async (userId: string, updates: Partial<UserListItem>) => {
      setSaving(userId)
      try {
        const updated = await updateUser(userId, updates)
        setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)))
        // 自分自身を編集した場合、ストアを再同期してHome画面に即反映
        if (userId === currentUserId) {
          await refreshUserData()
        }
      } catch (error) {
        console.error('Failed to update user:', error)
      } finally {
        setSaving(null)
      }
    },
    [currentUserId, refreshUserData],
  )

  const toggleDomain = useCallback(
    (user: UserListItem, domain: string) => {
      const newDomains = user.domains.includes(domain)
        ? user.domains.filter((d) => d !== domain)
        : [...user.domains, domain]
      handleUpdateUser(user.id, { domains: newDomains })
    },
    [handleUpdateUser],
  )

  const toggleFeature = useCallback(
    (user: UserListItem, feature: string) => {
      const newFeatures = user.features.includes(feature)
        ? user.features.filter((f) => f !== feature)
        : [...user.features, feature]
      handleUpdateUser(user.id, { features: newFeatures })
    },
    [handleUpdateUser],
  )

  const toggleFullAccess = useCallback(
    (user: UserListItem) => {
      handleUpdateUser(user.id, { has_full_access: !user.has_full_access })
    },
    [handleUpdateUser],
  )

  const toggleSafeMode = useCallback(
    (user: UserListItem) => {
      handleUpdateUser(user.id, { safe_mode: !user.safe_mode })
    },
    [handleUpdateUser],
  )

  const handleSaveAccess = useCallback(
    async (productIds: number[], episodeIds: number[]) => {
      if (!accessModalUser) return
      await handleUpdateUser(accessModalUser.id, { product_ids: productIds, episode_ids: episodeIds })
    },
    [accessModalUser, handleUpdateUser],
  )

  const handleDeleteUser = useCallback(async (userId: string) => {
    setDeleting(userId)
    try {
      await deleteUser(userId)
      setUsers((prev) => prev.filter((u) => u.id !== userId))
    } catch (error) {
      console.error('Failed to delete user:', error)
    } finally {
      setDeleting(null)
    }
  }, [])

  if (loading > 0) {
    return null
  }

  return (
    <>
      <Header title="ユーザー管理" onClose={() => navigate(PATHS.HOME)} />
      <div className="p-4 max-w-6xl mx-auto">
        <div className="mb-4 flex justify-end">
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            ユーザーを招待
          </Button>
        </div>
        <div className="space-y-3">
          {users.map((user) => (
            <Collapsible key={user.id}>
              <div className="border rounded-lg bg-card">
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left">
                  <div>
                    <div className="font-medium">{user.custom_name || user.name || user.email || 'Unknown'}</div>
                    {user.custom_name && <div className="text-xs text-muted-foreground">{user.name || user.email}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {user.has_full_access && (
                      <span className="inline-flex px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                        Full
                      </span>
                    )}
                    <ChevronIcon />
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="border-t px-4 pb-4 pt-3 space-y-4">
                    {/* カスタムネーム */}
                    <CustomNameInput
                      value={user.custom_name ?? ''}
                      onSave={(name) => handleUpdateUser(user.id, { custom_name: name || null })}
                      disabled={saving === user.id}
                    />

                    {/* 基本情報 */}
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>Email: {user.email ?? '-'}</div>
                      {user.last_accessed_at && (
                        <div>最終アクセス: {parseUtcDate(user.last_accessed_at).toLocaleString('ja-JP')}</div>
                      )}
                    </div>

                    {/* 権限設定 */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`full-access-${user.id}`}
                          checked={user.has_full_access}
                          onCheckedChange={() => toggleFullAccess(user)}
                          disabled={saving === user.id || deleting === user.id}
                        />
                        <label htmlFor={`full-access-${user.id}`} className="text-sm font-medium">
                          フルアクセス
                        </label>
                      </div>
                      {user.has_full_access && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`safe-mode-${user.id}`}
                            checked={user.safe_mode}
                            onCheckedChange={() => toggleSafeMode(user)}
                            disabled={saving === user.id || deleting === user.id}
                          />
                          <label htmlFor={`safe-mode-${user.id}`} className="text-sm font-medium">
                            Safeモード
                          </label>
                        </div>
                      )}
                    </div>

                    {!user.has_full_access && (
                      <>
                        <div>
                          <div className="text-sm font-medium mb-2">ドメインアクセス</div>
                          <div className="flex flex-wrap gap-2">
                            {availableDomains.map((domain) => (
                              <label
                                key={domain}
                                className="flex items-center gap-1 text-sm bg-muted px-2 py-1 rounded cursor-pointer"
                              >
                                <Checkbox
                                  checked={user.domains.includes(domain)}
                                  onCheckedChange={() => toggleDomain(user, domain)}
                                  disabled={saving === user.id}
                                />
                                {domain}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm font-medium mb-2">機能アクセス</div>
                          <div className="flex flex-wrap gap-2">
                            {availableFeatures.map((feature) => (
                              <label
                                key={feature}
                                className="flex items-center gap-1 text-sm bg-muted px-2 py-1 rounded cursor-pointer"
                              >
                                <Checkbox
                                  checked={user.features.includes(feature)}
                                  onCheckedChange={() => toggleFeature(user, feature)}
                                  disabled={saving === user.id}
                                />
                                {feature}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm font-medium mb-2">個別アクセス</div>
                          <div className="flex items-center gap-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setAccessModalUser(user)}
                              disabled={saving === user.id}
                            >
                              アクセス設定
                            </Button>
                            <div className="text-sm text-muted-foreground">
                              {user.product_ids.length > 0 && (
                                <span className="mr-2">プロダクト: {user.product_ids.length}件</span>
                              )}
                              {user.episode_ids.length > 0 && <span>エピソード: {user.episode_ids.length}件</span>}
                              {user.product_ids.length === 0 && user.episode_ids.length === 0 && <span>設定なし</span>}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* 削除ボタン */}
                    <div className="pt-2 border-t">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm" disabled={saving === user.id || deleting === user.id}>
                            {deleting === user.id ? '削除中...' : 'ユーザーを削除'}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>ユーザーを削除しますか？</AlertDialogTitle>
                            <AlertDialogDescription>
                              {user.custom_name || user.name || user.email || 'このユーザー'}
                              のすべてのデータが完全に削除されます。この操作は元に戻せません。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>キャンセル</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteUser(user.id)}>削除</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {saving === user.id && <div className="text-sm text-muted-foreground">保存中...</div>}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        {/* アクセス設定モーダル */}
        <AccessSettingsModal
          open={accessModalUser !== null}
          onOpenChange={(open) => !open && setAccessModalUser(null)}
          productIds={accessModalUser?.product_ids ?? []}
          episodeIds={accessModalUser?.episode_ids ?? []}
          onSave={handleSaveAccess}
        />

        <InviteUserDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          onInvited={(invited) => setUsers((prev) => [invited, ...prev])}
        />
      </div>
    </>
  )
}

const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-muted-foreground transition-transform [[data-state=open]_&]:rotate-180"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
)

const CustomNameInput = ({
  value,
  onSave,
  disabled,
}: {
  value: string
  onSave: (name: string) => void
  disabled: boolean
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const handleSave = () => {
    if (draft !== value) {
      onSave(draft)
    }
    setEditing(false)
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">カスタムネーム:</span>
        <span className="text-sm text-muted-foreground">{value || '未設定'}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft(value)
            setEditing(true)
          }}
          disabled={disabled}
        >
          編集
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium shrink-0">カスタムネーム:</span>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-8 max-w-48"
        placeholder="表示名を入力"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') setEditing(false)
        }}
      />
      <Button variant="outline" size="sm" onClick={handleSave}>
        保存
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
        取消
      </Button>
    </div>
  )
}
